import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getStatusColor } from '../../config/statusColors';
import { Card, Table, THead, TBody, Tr, Th, Td } from '../ui';

interface Project {
  id: number;
  project_id: string;
  title: string;
  title_ar?: string;
  status: string;
  status_display?: string;
  priority: string;
  priority_display?: string;
  progress_percentage: number;
  updated_at: string;
  client_profile_name?: string;
}

interface RecentProjectsProps {
  projects: Project[];
  showAllLink?: boolean;
}

const RecentProjects: React.FC<RecentProjectsProps> = ({
  projects,
  showAllLink = true,
}) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  // Canonical status tokens (STYLE_GUIDE DR-10) instead of the previous
  // ad-hoc Bootstrap color map.
  const getStatusBadge = (status: string, display?: string) => {
    const colors = getStatusColor(status, 'solid');
    return (
      <span
        className="badge"
        style={{
          backgroundColor: colors.background,
          color: colors.text,
          border: `1px solid ${colors.border}`,
        }}
      >
        {display || status}
      </span>
    );
  };

  const progressToken = (percentage: number) => {
    if (percentage >= 100) return 'var(--success)';
    if (percentage >= 50) return 'var(--primary)';
    return 'var(--warning)';
  };

  return (
    <Card
      className="overflow-hidden"
      header={(
        <div className="flex items-center justify-between">
          <h5 className="text-lg font-semibold mb-0">
            <i className="bi bi-kanban me-2"></i>
            {isRTL ? 'المشاريع الأخيرة' : 'Recent Projects'}
          </h5>
          {showAllLink && (
            <Link to="/projects" className="text-primary text-sm font-medium hover:underline">
              {t('viewAll')}
            </Link>
          )}
        </div>
      )}
    >
      <div className="overflow-x-auto -m-6">
        <Table hoverable className="mb-0">
          <THead>
            <Tr>
              <Th>{isRTL ? 'رقم المشروع' : 'Project ID'}</Th>
              <Th>{isRTL ? 'العنوان' : 'Title'}</Th>
              <Th>{isRTL ? 'التقدم' : 'Progress'}</Th>
              <Th>{t('status')}</Th>
              <Th>{t('lastModified')}</Th>
              <Th>{t('actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {projects.length === 0 ? (
              <Tr>
                <Td colSpan={6} className="text-center py-8 text-gray-500">
                  {isRTL ? 'لا توجد مشاريع' : 'No projects found'}
                </Td>
              </Tr>
            ) : (
              projects.map((project) => (
                <Tr key={project.id}>
                  <Td>
                    <span className="font-medium text-primary">{project.project_id}</span>
                  </Td>
                  <Td>{isRTL && project.title_ar ? project.title_ar : project.title}</Td>
                  <Td style={{ minWidth: '120px' }}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          role="progressbar"
                          style={{
                            width: `${project.progress_percentage}%`,
                            backgroundColor: progressToken(project.progress_percentage),
                          }}
                          aria-valuenow={project.progress_percentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                      <span className="text-xs text-gray-500 tabular-nums" style={{ minWidth: '35px' }}>
                        {project.progress_percentage}%
                      </span>
                    </div>
                  </Td>
                  <Td>{getStatusBadge(project.status, project.status_display)}</Td>
                  <Td className="whitespace-nowrap">
                    {project.updated_at
                      ? new Date(project.updated_at).toLocaleDateString()
                      : '-'}
                  </Td>
                  <Td>
                    <Link
                      to={`/projects/${project.id}`}
                      className="text-primary hover:text-primary-deep"
                      aria-label={t('actions')}
                    >
                      <i className="bi bi-eye"></i>
                    </Link>
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </div>
    </Card>
  );
};

export default RecentProjects;
